/** Workspace 360 — one workspace's artifacts, people, access, changes,
 *  exposure and gateway dependencies in a single view.
 *
 *  Replaces the old drill-down that went straight from the workspace list to a
 *  bare report list, which meant answering "who can see what in Finance, and
 *  what changed?" required visiting five separate pages and filtering each one
 *  by workspace by hand.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import {
  fetchWorkspaceChanges,
  fetchWorkspaceOverview,
  type ArtifactSummary,
  type ArtifactType,
  type WorkspaceOverview,
  type WorkspacePerson,
} from "../api/client";
import PageHeader from "../components/PageHeader";
import Tabs, { type TabItem } from "../components/Tabs";
import DataTable from "../components/DataTable";
import StatCard from "../components/StatCard";
import ErrorBanner from "../components/ErrorBanner";
import EmptyState from "../components/EmptyState";
import RegionBadge from "../components/RegionBadge";
import { LoadingPanel } from "../components/Spinner";
import ShowSystemToggle from "../components/ShowSystemToggle";
import ChangeCategoryBadge from "../components/ChangeCategoryBadge";
import ChangeSourceBadge from "../components/ChangeSourceBadge";
import ChangeActor from "../components/ChangeActor";
import { useDateFormat } from "../preferences/PreferencesProvider";
import { formatDateTime } from "../utils/datetime";

type TabId =
  | "overview"
  | "artifacts"
  | "people"
  | "changes"
  | "connections"
  | "exposure";

/**
 * Rows per page of the Changes tab, and how many the overview sends inline.
 *
 * Must match CHANGES_PAGE_SIZE in the workspaces router: the overview's
 * recentChanges is page one, so a different size here would make the pager
 * skip or repeat rows at the first boundary.
 */
const CHANGES_PAGE_SIZE = 100;

/** Plural labels for the artifact type filter, in the order the tiles use. */
const ARTIFACT_LABELS: Record<ArtifactType, string> = {
  report: "Reports",
  dataset: "Datasets",
  dataflow: "Dataflows",
  dashboard: "Dashboards",
  app: "Apps",
};

function typeTone(type: string): string {
  switch (type) {
    case "report":
      return "bg-info-subtle text-info-fg border-info-border";
    case "dataset":
      return "bg-warning-subtle text-warning-fg border-warning-border";
    case "dataflow":
      return "bg-success-subtle text-success-fg border-success-border";
    default:
      return "bg-surface-hover text-content-muted border-edge";
  }
}

/** Highest-privilege role a person holds, which is what an auditor reads first. */
function topRole(roles: string[]): string {
  const order = ["Admin", "Member", "Contributor", "Viewer", "Read"];
  for (const r of order) {
    if (roles.some((x) => x.toLowerCase() === r.toLowerCase())) return r;
  }
  return roles[0] ?? "—";
}

function roleTone(role: string): string {
  const r = role.toLowerCase();
  if (r === "admin") return "bg-danger-subtle text-danger-fg border-danger-border";
  if (r === "member" || r === "contributor")
    return "bg-warning-subtle text-warning-fg border-warning-border";
  return "bg-surface-hover text-content-muted border-edge";
}

export default function WorkspaceDetailPage() {
  const { workspaceId = "" } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();

  // The shared formatter, not a local new Date(): it honours the user's
  // timezone and date-format preferences, and it understands that the
  // activity table's sort key is a timestamp with a discriminator appended.
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => formatDateTime(value, dateFormat);

  const [data, setData] = useState<WorkspaceOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>("overview");
  const [showSystem, setShowSystem] = useState(false);
  const [typeFilter, setTypeFilter] = useState<ArtifactType | "all">("all");

  const openArtifacts = (type: ArtifactType) => {
    setTypeFilter(type);
    setTab("artifacts");
  };

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchWorkspaceOverview(workspaceId, showSystem)
      .then(setData)
      .catch((e) => setError(e?.message ?? "Failed to load workspace."))
      .finally(() => setLoading(false));
  }, [workspaceId, showSystem]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Changes tab paging ──
  // Page one rides along with the overview, so opening the tab costs nothing
  // and only paging past the first hundred rows makes a request. An
  // administrator's 30-day window here runs to tens of thousands of rows,
  // which is why they are not all sent at once.
  const [changesPage, setChangesPage] = useState(0);
  const [pagedChanges, setPagedChanges] = useState<Record<string, any>[]>([]);
  const [changesTotal, setChangesTotal] = useState<number | null>(null);
  const [changesLoading, setChangesLoading] = useState(false);
  const [changesError, setChangesError] = useState<string | null>(null);
  // Retrying the page it already failed on is not a state change, so the
  // effect needs something that does change to fire again.
  const [changesReload, setChangesReload] = useState(0);

  // Back to page one whenever the set underneath changes, or the pager would
  // be pointing at an offset into a differently-filtered list.
  useEffect(() => {
    setChangesPage(0);
    setPagedChanges([]);
    setChangesTotal(null);
    setChangesError(null);
  }, [workspaceId, showSystem]);

  useEffect(() => {
    if (tab !== "changes" || changesPage === 0) return;
    let cancelled = false;
    setChangesLoading(true);
    setChangesError(null);
    fetchWorkspaceChanges(workspaceId, {
      offset: changesPage * CHANGES_PAGE_SIZE,
      limit: CHANGES_PAGE_SIZE,
      showSystem,
    })
      .then((page) => {
        if (cancelled) return;
        setPagedChanges(page.items);
        // Only when the read reached the end of the window and so knows the
        // real figure. Otherwise the overview's count stands — same window,
        // same filters, and counted once instead of on every page turn.
        if (page.total !== null) setChangesTotal(page.total);
      })
      .catch((e) => {
        if (cancelled) return;
        setChangesError(e?.message ?? "Failed to load this page of changes.");
        setPagedChanges([]);
      })
      .finally(() => {
        if (!cancelled) setChangesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, workspaceId, showSystem, changesPage, changesReload]);

  const artifactColumns: ColumnDef<ArtifactSummary, string>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <span className="font-medium text-content">{row.original.name}</span>
      ),
    },
    {
      accessorKey: "artifactType",
      header: "Type",
      cell: ({ getValue }) => {
        const type = String(getValue());
        return (
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold capitalize ${typeTone(type)}`}
          >
            {type}
          </span>
        );
      },
    },
    {
      id: "userCount",
      header: "People",
      accessorFn: (row) => String(row.userCount ?? ""),
      cell: ({ row }) => (
        <span
          className="tabular-nums"
          title={
            row.original.userCount === null
              ? "No permission snapshot for this artifact — its access is unknown, not zero"
              : undefined
          }
        >
          {row.original.userCount ?? "—"}
        </span>
      ),
    },
    {
      id: "refresh",
      header: "Last Refresh",
      accessorFn: (row) => row.lastRefreshAt,
      cell: ({ row }) => {
        const { lastRefreshAt, refreshStatus } = row.original;
        if (!lastRefreshAt) return <span className="text-content-muted">—</span>;
        const failed = ["failed", "disabled"].includes(refreshStatus.toLowerCase());
        return (
          <span className={failed ? "text-danger-fg" : "text-content-muted"}>
            {formatDate(lastRefreshAt)}
            {failed ? ` (${refreshStatus})` : ""}
          </span>
        );
      },
    },
    {
      id: "modified",
      header: "Modified",
      accessorFn: (row) => row.modifiedDateTime,
      cell: ({ getValue }) => (
        <span className="text-content-muted">
          {getValue() ? formatDate(getValue()) : "—"}
        </span>
      ),
    },
    {
      id: "flags",
      header: "Flags",
      accessorFn: (row) => `${row.publiclyShared ? "public" : ""} ${row.source}`,
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-1">
          {row.original.publiclyShared && (
            <span className="inline-flex items-center rounded-full border border-danger-border bg-danger-subtle px-2 py-0.5 text-xs font-semibold text-danger-fg">
              Public
            </span>
          )}
          {/* Provenance per row, because a mixed workspace lists collected and
              live artifacts side by side and only one kind is evidence. */}
          {row.original.source === "live" && (
            <span
              className="inline-flex items-center rounded-full border border-info-border bg-info-subtle px-2 py-0.5 text-xs font-semibold text-info-fg"
              title="Read from the admin API just now — not collected evidence"
            >
              Live
            </span>
          )}
        </div>
      ),
    },
  ];

  const peopleColumns: ColumnDef<WorkspacePerson, string>[] = [
    {
      accessorKey: "displayName",
      header: "Name",
      cell: ({ row }) => row.original.displayName || row.original.email || "—",
    },
    { accessorKey: "email", header: "Email", cell: ({ getValue }) => getValue() || "—" },
    {
      id: "role",
      header: "Highest Role",
      accessorFn: (row) => topRole(row.roles),
      cell: ({ row }) => {
        const role = topRole(row.original.roles);
        return (
          <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${roleTone(role)}`}
            title={row.original.roles.join(", ")}
          >
            {role}
          </span>
        );
      },
    },
    {
      id: "allRoles",
      header: "All Roles",
      accessorFn: (row) => row.roles.join(", "),
      cell: ({ getValue }) => (
        <span className="text-xs text-content-muted">{getValue() || "—"}</span>
      ),
    },
    {
      accessorKey: "principalType",
      header: "Type",
      cell: ({ getValue }) => getValue() || "—",
    },
    {
      id: "artifactCount",
      header: "Artifacts",
      accessorFn: (row) => String(row.artifactCount),
      cell: ({ row }) => (
        <span className="tabular-nums">{row.original.artifactCount}</span>
      ),
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const changeColumns: ColumnDef<Record<string, any>, any>[] = [
    {
      accessorKey: "timestampActivity",
      header: "Date / Time",
      cell: ({ getValue }) => formatDate(getValue()),
    },
    {
      id: "source",
      header: "Source",
      accessorFn: (row) => row.changeSource ?? "",
      cell: ({ row }) => <ChangeSourceBadge changeSource={row.original.changeSource} />,
    },
    {
      id: "category",
      header: "Category",
      accessorFn: (row) => row.changeCategory ?? "",
      cell: ({ row }) => (
        <ChangeCategoryBadge changeCategory={row.original.changeCategory} />
      ),
    },
    { accessorKey: "activityType", header: "Activity", cell: ({ getValue }) => getValue() || "—" },
    {
      id: "actor",
      header: "Who",
      accessorFn: (row) =>
        row.actorEmail || (row.attributedActors ?? []).join(", ") || row.actorName || "",
      cell: ({ row }) => <ChangeActor {...row.original} />,
    },
    { accessorKey: "reportName", header: "Artifact", cell: ({ getValue }) => getValue() || "—" },
  ];

  if (loading && !data) return <LoadingPanel label="Loading workspace…" />;

  if (error && !data) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Workspace"
          breadcrumb={[{ label: "Workspaces", to: "/workspaces" }, { label: workspaceId }]}
        />
        <ErrorBanner message={error} onRetry={load} />
      </div>
    );
  }

  if (!data) return null;

  const ws = data.workspace;
  const admins = data.people.filter((p) => topRole(p.roles).toLowerCase() === "admin");

  // Whether anything has established who can reach this workspace, from a
  // collected snapshot or a live read. Without it every access figure is
  // unknown rather than zero, and the two must not look alike.
  const accessKnown = data.permissionsCollected || data.people.length > 0;

  const visibleArtifacts =
    typeFilter === "all"
      ? data.artifacts
      : data.artifacts.filter((a) => a.artifactType === typeFilter);

  const changeRowCount = changesTotal ?? data.changeCount30d;
  // The overview ships several pages' worth. When that covers the whole
  // window, the tab has everything: it can page and sort in the browser and
  // never ask the server again. That is the normal case for the roles whose
  // view is filtered, because the filter removes most of the rows.
  const haveEveryChange = data.recentChanges.length >= changeRowCount;
  const changeRows = haveEveryChange
    ? data.recentChanges
    : changesPage === 0
    ? data.recentChanges.slice(0, CHANGES_PAGE_SIZE)
    : pagedChanges;

  const tabs: readonly TabItem<TabId>[] = [
    { id: "overview", label: "Overview" },
    { id: "artifacts", label: "Artifacts", count: data.artifacts.length },
    { id: "people", label: "People & Access", count: data.people.length },
    // The whole 30-day window, which is what the stat card counts. It used to
    // be recentChanges.length — the size of the first page — so a workspace
    // with 452 changes showed a card reading 452 beside a tab reading 25.
    { id: "changes", label: "Changes", count: changeRowCount },
    { id: "connections", label: "Connections", count: data.gateways.length },
    { id: "exposure", label: "Exposure", count: data.publicArtifacts.length },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title={ws.name || workspaceId}
        subtitle={
          <span className="inline-flex flex-wrap items-center gap-2">
            <span>
              {ws.type || "Workspace"}
              {ws.state ? ` · ${ws.state}` : ""}
              {ws.monitored ? " · In SOX scope" : " · Not monitored"}
            </span>
            {/* Where this workspace's data physically sits — inherited from its
                capacity, and not shown anywhere in Power BI itself. */}
            <RegionBadge
              region={ws.region}
              source={ws.regionSource}
              capacityName={ws.capacityName}
              capacitySku={ws.capacitySku}
            />
            {ws.capacityName && (
              <span className="text-xs text-content-muted">
                on {ws.capacityName}
                {ws.capacitySku ? ` (${ws.capacitySku})` : ""}
              </span>
            )}
          </span>
        }
        breadcrumb={[
          { label: "Workspaces", to: "/workspaces" },
          { label: ws.name || workspaceId },
        ]}
        actions={<ShowSystemToggle show={showSystem} onChange={setShowSystem} />}
      />

      {error ? <ErrorBanner message={error} onRetry={load} tone="warning" /> : null}

      {/* Where these numbers came from. Only shown when it is not the normal
          case, because on a monitored workspace the provenance is unremarkable
          and a permanent banner would train people to ignore it. */}
      {(data.detailSource !== "collected" || data.detailNote) && (
        <div
          className={`rounded-xl border px-4 py-3 text-sm ${
            data.detailSource === "live" || data.detailSource === "mixed"
              ? "border-info-border bg-info-subtle text-info-fg"
              : "border-warning-border bg-warning-subtle text-warning-fg"
          }`}
        >
          <span className="font-semibold">
            {data.detailSource === "live"
              ? "Live read — not collected evidence. "
              : data.detailSource === "mixed"
                ? "Partly live — the gaps are not collected evidence. "
                : data.detailSource === "unavailable"
                  ? "Detail unavailable. "
                  : "Some detail could not be read. "}
          </span>
          {data.detailNote}
        </div>
      )}

      {/* Estate at a glance. Severity is driven by the things an auditor must
          act on: failed refreshes, public exposure, admin count. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        {/* Each tile opens the artifact list filtered to its own type, so a
            count is a way in rather than a dead end. */}
        <StatCard
          label="Reports"
          value={data.reportCount}
          onClick={() => openArtifacts("report")}
        />
        <StatCard
          label="Datasets"
          value={data.datasetCount}
          onClick={() => openArtifacts("dataset")}
        />
        <StatCard
          label="Dataflows"
          value={data.dataflowCount}
          onClick={() => openArtifacts("dataflow")}
        />
        <StatCard
          label="Apps"
          value={data.appCount}
          onClick={() => openArtifacts("app")}
        />
        {/* Only when something actually counted them. Nothing collects
            dashboards, so a permanent "0" here would be a claim we cannot
            support on every monitored workspace. */}
        {data.dashboardCount !== null && (
          <StatCard
            label="Dashboards"
            value={data.dashboardCount}
            onClick={() => openArtifacts("dashboard")}
          />
        )}
        <StatCard
          label="People with access"
          value={accessKnown ? data.people.length : "—"}
          onClick={() => setTab("people")}
        />
        <StatCard
          label="Changes (30d)"
          value={data.changeCount30d}
          onClick={() => setTab("changes")}
        />
        <StatCard
          label="Publicly shared"
          value={data.publicArtifacts.length}
          severity={data.publicArtifacts.length > 0 ? "critical" : "normal"}
          onClick={() => setTab("exposure")}
        />
      </div>

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      {tab === "overview" && (
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="rounded-xl border border-edge bg-surface-raised p-5">
            <h2 className="mb-3 text-sm font-semibold text-content">Access summary</h2>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-content-muted">People with any access</dt>
                <dd className="font-semibold text-content tabular-nums">
                  {/* The tile above already shows "—" when access is unknown;
                      printing 0 here beside it asserted "nobody has access",
                      which is a different and much stronger claim. */}
                  {accessKnown ? data.people.length : "—"}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-content-muted">Workspace admins</dt>
                <dd
                  className={`font-semibold tabular-nums ${
                    admins.length > 3 ? "text-warning-fg" : "text-content"
                  }`}
                >
                  {accessKnown ? admins.length : "—"}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-content-muted">Service principals / apps</dt>
                <dd className="font-semibold text-content tabular-nums">
                  {accessKnown
                    ? data.people.filter(
                        (p) =>
                          p.principalType &&
                          p.principalType.toLowerCase() !== "user"
                      ).length
                    : "—"}
                </dd>
              </div>
            </dl>
            {admins.length > 0 ? (
              <p className="mt-3 border-t border-edge pt-3 text-xs leading-relaxed text-content-muted">
                Admins: {admins.map((a) => a.email).join(", ")}
              </p>
            ) : null}
          </section>

          <section className="rounded-xl border border-edge bg-surface-raised p-5">
            <h2 className="mb-3 text-sm font-semibold text-content">Data health</h2>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-content-muted">Failed refreshes (7d)</dt>
                {/* Refresh history needs workspace membership, so for a
                    workspace the collector cannot read there is no answer.
                    Showing 0 would read as a clean bill of health. */}
                <dd
                  className={`font-semibold tabular-nums ${
                    (data.failedRefreshes7d ?? 0) > 0
                      ? "text-danger-fg"
                      : "text-content"
                  }`}
                  title={
                    data.refreshDataAvailable
                      ? undefined
                      : "No refresh history collected — the collector needs Viewer access on this workspace"
                  }
                >
                  {data.refreshDataAvailable ? data.failedRefreshes7d : "Not collected"}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-content-muted">Last refresh</dt>
                <dd className="text-content">{formatDate(data.lastRefreshAt)}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-content-muted">Gateways depended on</dt>
                <dd className="font-semibold text-content tabular-nums">
                  {data.gateways.length}
                </dd>
              </div>
            </dl>
          </section>
        </div>
      )}

      {tab === "artifacts" && (
        <div className="space-y-3">
          {/* Filter chips rather than a select: there are only ever five
              types, and the counts are the useful part. */}
          <div className="flex flex-wrap gap-2">
            {(["all", "report", "dataset", "dataflow", "dashboard", "app"] as const)
              .filter(
                (t) =>
                  t === "all" ||
                  data.artifacts.some((a) => a.artifactType === t)
              )
              .map((t) => {
                const count =
                  t === "all"
                    ? data.artifacts.length
                    : data.artifacts.filter((a) => a.artifactType === t).length;
                const active = typeFilter === t;
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTypeFilter(t)}
                    aria-pressed={active}
                    className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                      active
                        ? "border-accent bg-accent text-accent-fg"
                        : "border-edge bg-surface-raised text-content-muted hover:bg-surface-hover"
                    }`}
                  >
                    {t === "all" ? "All" : ARTIFACT_LABELS[t]} ({count})
                  </button>
                );
              })}
          </div>

          {visibleArtifacts.length === 0 ? (
            <EmptyState
              title={
                data.artifacts.length === 0
                  ? "No artifacts found"
                  : `No ${typeFilter === "all" ? "" : typeFilter} artifacts`
              }
              description={
                data.artifactsCollected
                  ? "Nothing has been collected or read for this workspace."
                  : "This workspace is not monitored, so its artifacts are only known from a live read of the admin API."
              }
            />
          ) : (
            <DataTable
              data={visibleArtifacts}
              columns={artifactColumns}
              initialPageSize={25}
              onRowClick={(a) =>
                navigate(
                  `/workspaces/${workspaceId}/artifacts/${a.artifactType}/${encodeURIComponent(a.artifactId)}`
                )
              }
            />
          )}
        </div>
      )}

      {tab === "people" &&
        (data.people.length === 0 ? (
          <EmptyState
            title={
              data.permissionsCollected
                ? "Nobody has access"
                : "Access not known"
            }
            description={
              data.permissionsCollected
                ? "A permission snapshot exists for this workspace and it lists no users."
                : "No permission snapshot has been collected for this workspace, and the live read returned no workspace-level users. Enable monitoring to collect per-artifact access."
            }
          />
        ) : (
          <DataTable data={data.people} columns={peopleColumns} initialPageSize={25} />
        ))}

      {tab === "changes" &&
        (changeRowCount === 0 ? (
          <EmptyState
            title="No changes in the last 30 days"
            description={
              showSystem
                ? "No activity has been collected for this workspace in that window."
                : "No human activity recorded. Microsoft and service-principal activity is hidden — use the switch above to include it."
            }
          />
        ) : (
          <div className="space-y-3">
            {changesError && (
              <ErrorBanner
                message={changesError}
                onRetry={() => setChangesReload((n) => n + 1)}
              />
            )}
            <DataTable
              data={changeRows}
              columns={changeColumns}
              initialPageSize={CHANGES_PAGE_SIZE}
              serverPagination={
                haveEveryChange
                  ? undefined
                  : {
                      totalRows: changeRowCount,
                      pageIndex: changesPage,
                      pageSize: CHANGES_PAGE_SIZE,
                      onPageChange: setChangesPage,
                      loading: changesLoading,
                    }
              }
            />
            <p className="text-xs text-content-subtle px-1">
              Last 30 days, newest first — the same window the Changes card counts.
              {!haveEveryChange && " Columns cannot be sorted while paging through the server."}
            </p>
          </div>
        ))}

      {tab === "connections" &&
        (data.gateways.length === 0 ? (
          /* The gateway list is derived from this workspace's datasets, so
             without collected detail there is nothing to derive it from.
             "Its data is cloud-only" would then be a conclusion drawn from an
             empty list rather than from evidence, and can be flatly wrong. */
          <EmptyState
            title={
              data.artifactsCollected
                ? "No gateway dependencies"
                : "Gateway dependencies not known"
            }
            description={
              data.artifactsCollected
                ? "No datasource in this workspace binds to an on-premises gateway, so its data is cloud-only."
                : "Datasource bindings are only collected for monitored workspaces, and the admin API does not expose them, so a live read cannot tell us either. Enable monitoring to find out."
            }
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {data.gateways.map((gw) => (
              <div
                key={gw.gatewayId}
                className="rounded-xl border border-edge bg-surface-raised p-4"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="min-w-0 truncate text-sm font-semibold text-content">
                    {gw.name}
                  </p>
                  {gw.source === "derived" ? (
                    <span
                      className="shrink-0 rounded-full bg-warning-subtle px-2 py-0.5 text-xs font-semibold text-warning-fg"
                      title="Identified from datasource bindings; the gateway API did not return this cluster"
                    >
                      Inferred
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 truncate font-mono text-xs text-content-subtle">
                  {gw.gatewayId}
                </p>
                <p className="mt-2 text-xs text-content-muted">
                  {gw.datasourceCount} datasource
                  {gw.datasourceCount !== 1 ? "s" : ""} in this workspace
                </p>
                {gw.regionsServed?.length > 1 && (
                  <p
                    className="mt-2 text-xs text-warning-fg"
                    title="This gateway also moves data for workspaces in another geography"
                  >
                    Shared across {gw.regionsServed.join(", ")}
                  </p>
                )}
              </div>
            ))}
          </div>
        ))}

      {tab === "exposure" &&
        (data.publicArtifacts.length === 0 ? (
          <EmptyState
            title="Nothing publicly shared"
            description="No report in this workspace is shared to the web or published beyond the tenant."
          />
        ) : (
          <div className="space-y-2">
            {data.publicArtifacts.map((a) => (
              <div
                key={String(a.reportId)}
                className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-danger-border bg-danger-subtle px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-danger-fg">
                    {a.reportName || a.reportId}
                  </p>
                  <p className="text-xs text-content-muted">
                    {a.shareType || "Publicly shared"} · detected{" "}
                    {formatDate(a.detectedAt)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/workspaces/${workspaceId}/artifacts/report/${encodeURIComponent(a.reportId)}`
                    )
                  }
                  className="text-xs font-medium text-accent hover:underline"
                >
                  Open report
                </button>
              </div>
            ))}
          </div>
        ))}
    </div>
  );
}
